import { useCallback, useEffect, useMemo, useRef } from "react";
import { useOptionalPageRegistration } from "~/routing/pageRegistrations/PageRegistrationContext";
import { useStartWorkflowInternal } from "~/workflows/StartWorkflowContext";
import type { CompleteWorkflow } from "~/workflows/workflowRegistrations";
import type { NoWorkflowParameter, Workflow } from "~/workflows/workflows";
type Input = unknown;
type Output = unknown;
export function useWorkflow<TWorkflow extends Workflow<Input, Output>>(workflow: TWorkflow): StartWorkflow<TWorkflow> {
    const startWorkflow = useStartWorkflowInternal();
    const pageRegistration = useOptionalPageRegistration();
    // Capture the workflow so that useCallback doesn't need it in the dependency array. This means the hook doesn't respond to changes to this parameter type
    // which is not something any consumers should be doing.
    const workflowRef = useRef(workflow);
    const abortSignal = useAbortSignal();
    return useCallback<StartWorkflow<TWorkflow>>(
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    ((inputOrOnWorkflowCompleted: Input | CompleteWorkflow<Output> | undefined, possibleOnWorkflowCompleted: undefined | CompleteWorkflow<Output>) => {
        const { input, callback } = getInputAndCallback();
        startWorkflow(input, workflowRef.current, callback, abortSignal, pageRegistration);
        // This callback could either be
        // 1. (input, callback) => void;
        // 2. (input) => void
        // 3. (callback) => void;
        // 4. () => void;
        // We can use the number of arguments to determine which case we are in, with the exception of 2 and 3, where we instead need to use runtime information
        // We have constrained `input` so that it cannot be a function, and since we know callback is a function, we can use that to distinguish between 2 and 3
        function getInputAndCallback(): {
            input: Input;
            callback: CompleteWorkflow<Output>;
        } {
            if (possibleOnWorkflowCompleted !== undefined) {
                // case 1
                return { input: inputOrOnWorkflowCompleted, callback: possibleOnWorkflowCompleted };
            }
            else if (inputOrOnWorkflowCompleted === undefined) {
                // case 4
                return { input: undefined, callback: () => { } };
            }
            else if (typeof inputOrOnWorkflowCompleted === "function") {
                // case 3
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                return { input: undefined, callback: inputOrOnWorkflowCompleted as CompleteWorkflow<Output> };
            }
            // case 2
            return { input: inputOrOnWorkflowCompleted, callback: () => { } };
        }
    }) as StartWorkflow<TWorkflow>, [startWorkflow, abortSignal, pageRegistration]);
}
export type StartWorkflow<TWorkflow extends Workflow<unknown, unknown>> = TWorkflow extends Workflow<infer Input, infer Output> ? StartWorkflowFromInputOutputTypes<Input, Output> : never;
// This callback could either be
// 1. (input, callback) => void;
// 2. (input) => void
// 3. (callback) => void;
// 4. () => void;
type StartWorkflowFromInputOutputTypes<Input, Output> = Input extends NoWorkflowParameter ? Output extends NoWorkflowParameter ? () => void // case 4
 : (onWorkflowCompleted: CompleteWorkflow<Output>) => void // case 3
 : Output extends NoWorkflowParameter ? (input: NotAFunctionOrUndefined<Input>) => void // case 2
 : (input: NotAFunctionOrUndefined<Input>, onWorkflowCompleted: CompleteWorkflow<Output>) => void; // case 1
// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
type NotAFunctionOrUndefined<T> = T extends Function | undefined ? never : T;
function useAbortSignal() {
    const abortController = useMemo(() => new AbortController(), []);
    useEffect(() => () => {
        if (!abortController.signal.aborted) {
            abortController.abort();
        }
    }, [abortController]);
    return abortController.signal;
}
